import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import * as moment from 'moment';
import { now } from 'moment';
import { BehaviorSubject, combineLatest, from, Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, distinctUntilKeyChanged, filter, map, mergeMap, shareReplay, take, tap } from 'rxjs/operators';
import { CustomAlertService } from 'src/app/_components/custom-alert/custom-alert.service';
import { DynamicBarService } from 'src/app/_components/dynamic-bar/dynamic-bar.service';
import { ComentariosService } from 'src/app/_components/glm-comentarios/comentarios.service';
import { Comentario } from 'src/app/_models/comentario';
import { Evento } from 'src/app/_models/evento';
import { Criterio, Meta } from 'src/app/_models/meta';
import { GLMLanguageService } from 'src/app/_services/glm-language.service';
import { EstudioService } from '../estudio.service';
import { RetroalimentacionService } from '../retroalimentacion/_services/retroalimentacion.service';
import { Autoevaluacion } from './autoevaluacion';
import { AutoevaluacionService } from './autoevaluacion.service';

@Component({
  selector: 'app-autoevaluacion',
  standalone: false,
  templateUrl: './autoevaluacion.component.html',
  styleUrls: ['./autoevaluacion.component.scss']
})
export class AutoevaluacionComponent implements OnInit, OnDestroy {

  criterios$: Observable<Criterio[]>;

  eventoUnidadId = +this.route.snapshot.paramMap.get('eventoId');
  eventoMateriaId = +this.route.snapshot.paramMap.get('eventoMateriaId');
  alumnoId = +this.route.snapshot.paramMap.get('alumnoId');

  evento$: Observable<Evento>;

  autoevaluacion$: Observable<Autoevaluacion>;

  comentarioData$: Observable<{comentarios: any[], targetId: string, editable: boolean}>;

  autoevaluacionId: number;

  guardandoSubject = new BehaviorSubject(false);
  guardando$ = this.guardandoSubject.asObservable();
  expandidoSubject = new BehaviorSubject(false);
  expandido$ = this.expandidoSubject.asObservable();
  evaluacionSubscription: Subscription;

  textComentarioForm: UntypedFormControl = new UntypedFormControl('');

  @ViewChild('anchor') anchor: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private estudioService: EstudioService,
    private languageService: GLMLanguageService,
    private dynamicBarService: DynamicBarService,
    private titleService: Title,
    private autoevaluacionService: AutoevaluacionService,
    private customAlertService: CustomAlertService,
    private comentariosService: ComentariosService
    ) { }

  ngOnInit(): void {
    this.autoevaluacion$ = this.setAutoevaluacionObservable(this.eventoMateriaId, this.alumnoId);

    this.evento$ = this.autoevaluacion$.pipe(
      tap(data => {
        this.titleService.setTitle(`${data.evento.nombre} - Estudio`);
        this.languageService.setLanguage({nickName: data.evento['unidad'].idioma});
        this.dynamicBarService.setBarra('estudio-retroalimentacion', {unidad: data.evento['unidad']});
      }),
      map(data => {
        return data.evento;
      }),
      shareReplay(1)
    );

    this.comentarioData$ = this.observableComentario(this.autoevaluacion$);

    const metas$ = this.evento$.pipe(
      map(data => data.metas));

    this.criterios$ = combineLatest([metas$, this.languageService.getLanguageObservable(), this.autoevaluacion$]).pipe(
      map(([data, language, autoevaluacion]) => {
        let metasLang;
        if (language.activeLang) {
          metasLang = this.languageService.setIdiomaMetas(data, language.activeLang.nickName);
        }
        return this.filtrarCriteriosNoEvaluados(this.crearListadoCriterios(metasLang), autoevaluacion);
      }),
      shareReplay(1)
    );
  }

  setAutoevaluacionObservable(eventoMateriaId, alumnoId) {
    return this.autoevaluacionService.getAutoevaluacionAlumno(eventoMateriaId, alumnoId).pipe(
      map((autoevaluacion) => {
        if ((typeof autoevaluacion === 'boolean')) {
          return null;
        } else {
          this.textComentarioForm.setValidators(
            autoevaluacion.eventoMateriaAlumnoConfig.requiereAutoevaluacionComentario ? [Validators.required] : []);
          this.autoevaluacionId = autoevaluacion.id;
          return autoevaluacion;
        }
      }),
      shareReplay(1)
    );
  }

  filtrarCriteriosNoEvaluados(criterios: Criterio[], autoevaluacion) {
    // TODO: Quitar el condicional de completitud si llegan los niveles antes de estar completa la autoevaluación
    if (autoevaluacion.eventoMateriaAlumnoConfig.completoAutoevaluacion) {
      return criterios.filter(item => autoevaluacion.criterioNivel.map(nivel => nivel.criterioParentID).includes(item.id));
    }
    return criterios;
  }

  crearListadoCriterios(metas: Meta[]) {
    let resp = [];
    // Add the meta data to be used in the table and the description of the parent goal.
    metas.forEach(element => {
      element.criterios.forEach(criterio => {
        criterio.metaData = {
          codigo: element.codigo + '-' + element.id,
          desc: element.txtIdioma
        };
      });
      resp = resp.concat(element.criterios);
    });
    return resp;
  }

  observableComentario(autoevaluacion$: Observable<Autoevaluacion>):
    Observable<{targetId: string, comentarios: Comentario[], editable: boolean}> {
    return  autoevaluacion$.pipe(
      map(data => {
        return {completoAutoevaluacion: data.eventoMateriaAlumnoConfig.completoAutoevaluacion,
          eventoMateriaAlumnoId: data.eventoMateriaAlumnoConfig.eventoMateriaAlumnoId };
      })
    ).pipe(
      distinctUntilChanged(((prev, curr) => {
        return prev.completoAutoevaluacion === curr.completoAutoevaluacion;
       } )),
      mergeMap(eventoMateriaAlumnoConfig => {
        if (eventoMateriaAlumnoConfig.eventoMateriaAlumnoId === 0) {
          return of({targetId: '', comentarios: [], editable: false});
        } else {
          return combineLatest(([
            this.comentariosService.getComentariosPorObjeto(eventoMateriaAlumnoConfig.eventoMateriaAlumnoId, 'unidadeventoalumno'),
            this.comentariosService.getComentariosPorObjeto(eventoMateriaAlumnoConfig.eventoMateriaAlumnoId, 'UnidadEventoAlumnoAutoevaluacion')
          ])).pipe(
            map(([evento, autoevaluacion]) => {
              return evento.concat(autoevaluacion);
            })
          ).pipe(
            map(comentarios => {
              return {targetId: '', comentarios, editable: true};
            }),
            catchError(err => {
              this.customAlertService.setCustomMessage({
                tipo: 'error',
                texto: `Error al traer los comentarios. <br> Código de error: ${err.statusText}`
              });
              return of({targetId: '', comentarios: [], editable: false});
            })
          );
        }
      }),
    );
  }

  submit(autoevaluacion: Autoevaluacion, evento: Evento) {
    const formValid = this.textComentarioForm.valid;
    const criterios = this.contarCriterios(evento.metas);
    const evaluacionCompleta = !autoevaluacion.eventoMateriaAlumnoConfig.requiereAutoevaluacion
      || (autoevaluacion.criteriosAutoevaluacion.length === criterios);
    if (formValid && evaluacionCompleta) {
      this.guardandoSubject.next(true);
      of(autoevaluacion.eventoMateriaAlumnoConfig.requiereAutoevaluacionComentario).pipe(
        mergeMap(data => {
          const model = {
            comentario: new Comentario({
              id: '0',
              comentario: this.textComentarioForm.value,
              fecha: moment().format()
            }), targetID: autoevaluacion.eventoMateriaAlumnoConfig.eventoMateriaAlumnoId, tipo: 'UnidadEventoAlumnoAutoevaluacion'
          };
          return data ? this.comentariosService.guardarComentario(
            model
          ) : of([]);
        }),
        mergeMap(data => {
          return this.autoevaluacionService.setAutoevaluacionCompleta(
            autoevaluacion.eventoMateriaAlumnoConfig.id, autoevaluacion.id, autoevaluacion.unidadEventoMateriaId);
        })
      )
      .subscribe(res => {
        setTimeout(() => {this.anchor.nativeElement.scrollIntoView({behavior: 'smooth'}); }, 50);
        this.guardandoSubject.next(false);
      });
    } else {
      this.customAlertService.setCustomMessage({
        tipo: 'error',
        texto: `Por favor complete la evaluación para continuar.`
      });
    }
  }

  contarCriterios(metas: Meta[]): number {
    let criterios = 0;
    metas.forEach(meta => {
      meta.criterios.forEach(() => {
        criterios++;
      });
    });
    return criterios;
  }

  expandir(next: boolean) {
    this.expandidoSubject.next(next);
  }

  ngOnDestroy(): void {
    if (this.evaluacionSubscription) {
      this.evaluacionSubscription.unsubscribe();
    }
  }

}
